import { useState, useEffect } from 'react';
import { X, ShieldAlert, AlertTriangle } from 'lucide-react';
import type { QualityGateVerdict } from '../lib/api/types.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';

interface QualityGateOverrideDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Quality-Gate verdict being overridden — for context in the modal. */
  metadata: QualityGateVerdict | null | undefined;
  /** Caller submits the override; receives the operator's reason. */
  onSubmit: (reason: string) => Promise<void>;
}

const MIN_REASON_CHARS = 10;

export function QualityGateOverrideDialog({
  isOpen,
  onClose,
  metadata,
  onSubmit,
}: QualityGateOverrideDialogProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_CHARS;
  const issuesCount = metadata?.issues?.length ?? 0;
  const criticalCount = (metadata?.issues || []).filter(i => i.severity === 'CRITICAL').length;

  const handleSubmit = async () => {
    setError(null);
    if (tooShort) {
      setError(`Reason must be at least ${MIN_REASON_CHARS} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Override failed');
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} canClose={!submitting} size="xl">
      <ModalHeader>
        <div className="flex items-center space-x-3">
          <ShieldAlert className="h-6 w-6 text-warning-700" />
          <h2 className="text-xl sm:text-2xl font-bold text-grey-900">Override Quality Gate verdict</h2>
        </div>
        <button
          onClick={onClose}
          disabled={submitting}
          className="text-grey-400 hover:text-grey-600 transition-colors disabled:opacity-50 touch-target"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>

      <ModalBody className="space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-warning-900">
              <p className="font-semibold mb-1">You're approving an article the gate flagged as needing revision.</p>
              <p>
                The override is recorded against this article (operator,
                reason, timestamp) and visible to anyone reviewing it
                later. The article still needs to be Published as a
                separate step.
              </p>
            </div>
          </div>

          {metadata && (
            <div className="text-sm text-grey-700">
              <p className="mb-1">
                <span className="font-semibold">Verdict:</span> {metadata.verdict}
                {metadata.risk_tier && ` · risk ${metadata.risk_tier.toLowerCase()}`}
              </p>
              <p>
                <span className="font-semibold">Issues:</span> {issuesCount}
                {criticalCount > 0 && ` (${criticalCount} critical)`}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-grey-900 mb-1.5">
              Why are you overriding? <span className="text-danger-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. truth-check flagged a verbatim attribution that's actually correct per the announcement quote at p.3 — verified manually."
              rows={4}
              className="w-full p-3 border border-grey-200 rounded-lg text-sm leading-relaxed focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              disabled={submitting}
            />
            <p className={`mt-1 text-xs ${tooShort ? 'text-grey-500' : 'text-success-600'}`}>
              {trimmed.length} / {MIN_REASON_CHARS}+ characters
            </p>
          </div>

          {error && (
            <div className="p-3 bg-danger-50 border border-danger-200 text-danger-700 rounded-lg text-sm">
              {error}
            </div>
          )}
      </ModalBody>

      <ModalFooter className="bg-grey-50">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold text-grey-700 hover:bg-grey-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || tooShort}
            className="px-4 py-2 text-sm font-semibold text-white bg-warning-600 hover:bg-warning-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Overriding…' : 'Override and approve'}
          </button>
      </ModalFooter>
    </Modal>
  );
}
